import { FC, type Ref, useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router";
import { useAppSelector } from "@/hooks/storeHooks";
import { useTranslation } from "react-i18next";
import { MdOutlineWhatsapp } from "react-icons/md";
import { ImLinkedin2 } from "react-icons/im";
import { CiMail } from "react-icons/ci";
import { Button } from "@/components/ui/button";
import zooozHeader from "@/assets/zoooz_header.svg";
import TokenView from "@/dev/ui/token-view";
import { AnimatePresence, motion } from "framer-motion";
import { LogOut } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { useAppScrollContainer } from "@/hooks/context/app-scroll-context";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

// --------------------------------------------------
// Types & CTA data
// --------------------------------------------------

interface CTADataType {
  href: string;
  icon: FC<any>;
  label: string; // Accessible name for screen readers
}

const CTA_DATA: CTADataType[] = [
  {
    href: "https://wa.me/+972539427400?text=%D7%A9%D7%9C%D7%95%D7%9D%2C%20%D7%90%D7%A0%D7%99%20%D7%9E%D7%A2%D7%95%D7%A0%D7%99%D7%99%D7%9F%20%D7%9C%D7%A7%D7%91%D7%9C%20%D7%9E%D7%99%D7%93%D7%A2%20%D7%A0%D7%95%D7%A1%D7%A3",
    icon: MdOutlineWhatsapp,
    label: "WhatsApp", // 🔊 narrated by screen readers
  },
  {
    href: "https://www.linkedin.com/company/zoooz", // TODO: adjust real page
    icon: ImLinkedin2,
    label: "LinkedIn",
  },
  {
    href: "mailto:info@zoooz.co",
    icon: CiMail,
    label: "Send Email to info@zoooz.co",
  },
];

// --------------------------------------------------
// Navigation links (based on auth state)
// --------------------------------------------------
type LinkType = {
  to: string;
  text: string;
};
const LINKS_NOT_LOGIN: LinkType[] = [
  { to: "/subscription", text: "prices" },
  { to: "/login", text: "login" },
  { to: "/signup", text: "signup" },
];

const MOBILE_NAV_COLLAPSE_SCROLL_TOP = 96;
const MOBILE_NAV_EXPAND_SCROLL_TOP = 16;
// The nav's 1px bottom border is outside this auto-sized inner element.
const MOBILE_NAV_EXPANDED_INNER_HEIGHT = 119;
const MOBILE_NAV_COMPACT_INNER_HEIGHT = 55;

// --------------------------------------------------
// Component
// --------------------------------------------------

const NavBar: FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  const appScrollContainer = useAppScrollContainer();
  const [isCompact, setIsCompact] = useState(false);
  const activeScrollTarget = useRef<HTMLElement | null>(null);
  const mobileNavRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (!isMobile) {
      setIsCompact(false);
      activeScrollTarget.current = null;
      return;
    }

    const root = appScrollContainer?.current;
    if (!root) return;

    const updateCompactState = (target: HTMLElement) => {
      const latest = Math.max(0, target.scrollTop);

      setIsCompact((current) => {
        if (!current && latest >= MOBILE_NAV_COLLAPSE_SCROLL_TOP) {
          activeScrollTarget.current = target;
          return true;
        }

        const activeTarget = activeScrollTarget.current;
        const canExpand = activeTarget === target || !activeTarget?.isConnected;
        if (current && canExpand && latest <= MOBILE_NAV_EXPAND_SCROLL_TOP) {
          activeScrollTarget.current = null;
          return false;
        }

        return current;
      });
    };

    const handleScroll = (event: Event) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;
      if (target !== root && !root.contains(target)) return;
      if (target !== root && target.scrollHeight <= target.clientHeight + 1) return;

      updateCompactState(target);
    };

    updateCompactState(root);
    // Scroll events do not bubble. Capture lets the navbar follow pages that
    // intentionally own their own main scroll area (lists, company pages, etc.).
    root.addEventListener("scroll", handleScroll, { capture: true, passive: true });
    return () => root.removeEventListener("scroll", handleScroll, true);
  }, [isMobile, appScrollContainer]);

  useEffect(() => {
    setIsCompact(false);
    activeScrollTarget.current = null;
  }, [location.pathname]);

  useEffect(() => {
    if (!isMobile) return;

    const root = appScrollContainer?.current;
    const mobileNav = mobileNavRef.current;
    if (!root || !mobileNav) return;

    const syncVisibleBoundary = () => {
      // This boundary only positions sticky controls / focused content. Never
      // use it to resize the scroll viewport or its padding during animation.
      root.style.setProperty("--mobile-nav-visible-height", `${mobileNav.getBoundingClientRect().height}px`);
    };

    const observer = new ResizeObserver(syncVisibleBoundary);
    observer.observe(mobileNav);
    syncVisibleBoundary();

    return () => {
      observer.disconnect();
      root.style.removeProperty("--mobile-nav-visible-height");
    };
  }, [isMobile, appScrollContainer]);

  return (
    <>
      <MobileNavBar navRef={mobileNavRef} isCompact={isCompact} onLogoClick={() => navigate("/home")} />
      <DesktopNavBar onLogoClick={() => navigate("/home")} />
    </>
  );
};

export default NavBar;

const DesktopNavBar = ({ onLogoClick }: { onLogoClick: () => void }) => {
  const { user } = useAppSelector((state) => state.user);
  const isSignedIn = user !== null;

  return (
    <nav
      dir="rtl"
      className="hidden h-[120px] w-full overflow-hidden border-b border-zoooz-primary/10 bg-purple-50/95 shadow-sm backdrop-blur-xl md:block"
      aria-label="ניווט ראשי"
    >
      <div className="relative mx-auto h-full w-full max-w-screen-2xl">
        <img
          onClick={onLogoClick}
          draggable={false}
          src={zooozHeader}
          alt="Zoooz home page logo"
          className="absolute right-8 top-1.5 h-16 w-auto cursor-pointer select-none object-contain lg:right-12"
        />

        <div className="absolute left-8 top-5 lg:left-12">
          <SocialLinks />
        </div>

        <div className="absolute inset-x-8 top-[76px] flex h-9 items-center justify-between gap-4 border-t border-zoooz-primary/10 pt-1 lg:inset-x-12">
          {isSignedIn ? (
            <>
              <AccountSummary />
              <ZooozLogout />
            </>
          ) : (
            <AuthLinks />
          )}
        </div>
      </div>
    </nav>
  );
};

const MobileNavBar = ({
  navRef,
  isCompact,
  onLogoClick,
}: {
  navRef: Ref<HTMLElement>;
  isCompact: boolean;
  onLogoClick: () => void;
}) => {
  const { user } = useAppSelector((state) => state.user);
  const isSignedIn = user !== null;

  return (
    <nav
      ref={navRef}
      dir="rtl"
      style={{ paddingTop: "env(safe-area-inset-top, 0px)" }}
      className={`w-full overflow-hidden border-b transition-[background-color,border-color,box-shadow,backdrop-filter] duration-300 md:hidden ${isCompact
          ? "border-transparent bg-purple-50/65 shadow-none backdrop-blur-md"
          : "border-zoooz-primary/10 bg-purple-50/95 shadow-sm backdrop-blur-xl"
        }`}
      aria-label="ניווט ראשי"
      data-compact={isCompact}
    >
      <motion.div
        initial={false}
        animate={{ height: isCompact ? MOBILE_NAV_COMPACT_INNER_HEIGHT : MOBILE_NAV_EXPANDED_INNER_HEIGHT }}
        transition={{ type: "spring", stiffness: 190, damping: 28, mass: 0.9 }}
        style={{ willChange: "height" }}
        className="relative w-full overflow-hidden"
      >
        <motion.img
          onClick={onLogoClick}
          draggable={false}
          src={zooozHeader}
          alt="Zoooz home page logo"
          animate={{ height: isCompact ? 40 : 64, top: isCompact ? 8 : 6 }}
          transition={{ type: "spring", stiffness: 190, damping: 28, mass: 0.9 }}
          className="absolute right-3 w-auto max-w-[44%] cursor-pointer select-none object-contain"
        />

        <motion.div
          initial={false}
          animate={{ top: isCompact ? 12 : 20 }}
          transition={{ type: "spring", stiffness: 190, damping: 28, mass: 0.9 }}
          className="absolute left-3 h-8 w-[calc(100%-7rem)]"
        >
          <AnimatePresence initial={false}>
            {isCompact ? (
              <motion.div
                key="compact-account"
                initial={{ opacity: 0, x: -6 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -6 }}
                transition={{ duration: 0.22, ease: "easeOut" }}
                className="absolute left-0 top-0 flex min-w-0 items-center justify-start gap-1.5"
              >
                {isSignedIn ? (
                  <>
                    <AccountSummary compact />
                    <ZooozLogout />
                  </>
                ) : (
                  <AuthLinks compact />
                )}
              </motion.div>
            ) : (
              <motion.div
                key="social-actions"
                initial={{ opacity: 0, x: -6 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -6 }}
                transition={{ duration: 0.22, ease: "easeOut" }}
                className="absolute left-0 top-0"
              >
                <SocialLinks />
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        <AnimatePresence initial={false}>
          {!isCompact && (
            <motion.div
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.24, ease: "easeOut" }}
              className="absolute inset-x-3 top-[76px] flex h-9 items-center justify-between gap-3 border-t border-zoooz-primary/10 pt-1"
            >
              {isSignedIn ? (
                <>
                  <AccountSummary />
                  <ZooozLogout />
                </>
              ) : (
                <AuthLinks />
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </nav>
  );
};

const SocialLinks = () => (
  <div className="flex items-center gap-2" dir="ltr">
    {CTA_DATA.map(({ href, icon: Icon, label }) => (
      <a
        key={label}
        href={href}
        aria-label={label}
        target={href.startsWith("http") ? "_blank" : undefined}
        rel={href.startsWith("http") ? "noopener noreferrer" : undefined}
        className="flex size-9 items-center justify-center rounded-full bg-slate-800 text-white shadow-sm transition-colors hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-zoooz-primary/50"
      >
        <Icon aria-hidden="true" size={18} />
      </a>
    ))}
  </div>
);

const AccountSummary = ({ compact = false }: { compact?: boolean }) => {
  const { user } = useAppSelector((state) => state.user);
  const { t } = useTranslation();

  return (
    <div className="flex min-w-0 items-center gap-3 text-zoooz-text ">
      <span dir="auto" className={`${compact ? "max-w-20 text-xs" : "max-w-44 text-sm"} truncate font-bold`}>
        {t("welcomeUser", { name: user?.given_name })}
      </span>
      <TokenView>
        <TokenView.Content>
          <Popover>
            <PopoverTrigger>
              <TokenView.Icon className={compact ? "size-6" : "size-7"} />
            </PopoverTrigger>
            <PopoverContent dir="rtl">
              <div className="text-xs ">
                זוזים  משמשים לרכישת פרטי התקשרות עם משקיעי IMACT. זוזים מתחדשים בכל חודש באופן אוטומטי!
              </div>
            </PopoverContent>
          </Popover>

          <TokenView.Number className="text-xs font-bold text-zoooz-primary" />
        </TokenView.Content>
      </TokenView>
    </div>
  );
};

const ZooozLogout = () => {
  const { t } = useTranslation();

  return (
    <Button
      asChild
      size="sm"
      className={`${ZOOOZ_PILL_BASE_CLASSES} ${ZOOOZ_PILL_ACTIVE_CLASSES} h-8 gap-1 px-3 text-xs`}
    >
      <Link to="/logout" aria-label={t("logout")}>
        <LogOut className="size-3.5 text-white" />
        <span className="text-white">{t("logout")}</span>
      </Link>
    </Button>
  );
};

const AuthLinks = ({ compact = false }: { compact?: boolean }) => {
  const { t } = useTranslation();
  const location = useLocation();

  return (
    <div className={`flex items-center ${compact ? "gap-1" : "gap-1.5"}`} dir="rtl">
      {LINKS_NOT_LOGIN.map(({ to, text }) => {
        const isActive = location.pathname === to || location.pathname.startsWith(`${to}/`);

        return (
          <Button
            key={to}
            asChild
            size="sm"
            className={`${ZOOOZ_PILL_BASE_CLASSES} ${isActive ? ZOOOZ_PILL_ACTIVE_CLASSES : ZOOOZ_PILL_INACTIVE_CLASSES
              } ${compact ? "h-7 px-2 text-[10px]" : "h-8 px-3 text-xs"}`}
          >
            <Link to={to} aria-current={isActive ? "page" : undefined} className="whitespace-nowrap">
              <span className={isActive ? 'text-white' : ''}>
                {t(text)}
              </span>
            </Link>
          </Button>
        );
      })}
    </div>
  );
};

const ZOOOZ_PILL_BASE_CLASSES =
  "rounded-full border font-bold transition-all duration-200 focus-visible:ring-zoooz-primary/30 active:scale-95";
const ZOOOZ_PILL_ACTIVE_CLASSES =
  "border-white/40 bg-gradient-to-l from-zoooz-primary to-zoooz-secondary text-white shadow-[0_3px_10px_rgba(108,33,227,0.24)] ring-2 ring-zoooz-secondary/20 hover:from-zoooz-primary/90 hover:to-zoooz-secondary/90 hover:text-white";
const ZOOOZ_PILL_INACTIVE_CLASSES =
  "border-zoooz-primary/20 bg-white/80 text-zoooz-text shadow-sm hover:border-zoooz-primary/40 hover:bg-purple-100 hover:text-zoooz-primary";
